import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from '../../../config/api';
import {
  CREATE_BUDGET_AT_CLIENT_STEP_PATH,
  CREATE_BUDGET_PATH,
} from '../../../contants';
import {
  MONTHLY_OPTIONS,
  TYPE_OPTIONS,
  WEEKLY_OPTIONS,
} from '../../../contants/createBudget';
import { MESSAGE_ERROR } from '../../../contants/messageError';
import {
  addFormData,
  changeChoosenAccounts,
  changeChoosenCampaigns,
  changeChoosenSources,
  changeTab,
  resetState,
  setCurrentStep,
  setUniqueNameError,
} from '../../../features/createBudget/createBudgetSlice';
import { error, notyfError } from '../../../helpers/notyf';

export const useBudgetApi = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { clients, formData } = useSelector(state => state.createBudget);

  const fetchBudgetData = useCallback(async (budgetId) => {
    if (!budgetId || isNaN(budgetId) || clients.length === 0) {
      return;
    }

    try {
      const data = await api.GET_BUDGET(budgetId);
      let dataToSet = {
        ...formData,
        id: +data.id,
        amount: +data.amount,
        name: data.name,
        google_budget: data.google_budget,
        client: clients.find(client => +client.value === +data.client.id),
        type: data.type,
        renew_type: data.renew_type,
      };

      if (data.month_start_day) {
        dataToSet.month_start_day = MONTHLY_OPTIONS.find(
          opt => +opt.value === +data.month_start_day,
        );
      }
      
      if (data.week_start_day) {
        dataToSet.week_start_day = WEEKLY_OPTIONS.find(
          opt => +opt.value === +data.week_start_day,
        );
        dispatch(changeTab('weekly'));
      }
      if (data.start_date && data.end_date) {
        dataToSet.renew_type = TYPE_OPTIONS.find(
          opt => opt.label === data.renew_type,
        );
        dataToSet.start_date = data.start_date;
        dataToSet.end_date = data.end_date;
        dispatch(changeTab('custom'));
      }
      
      dispatch(addFormData({ ...dataToSet }));

      if (data.campaigns.length) {
        dispatch(changeChoosenCampaigns(data.campaigns));
        dispatch(changeChoosenAccounts([data.campaigns[0]?.account]));
        dispatch(
          changeChoosenSources([data.campaigns[0]?.account?.source]),
        );
      }
    } catch (err) {
      const { response } = err;
      if (response?.status === 404) {
        notyfError("Budget isn't existed");
        navigate(CREATE_BUDGET_AT_CLIENT_STEP_PATH, { replace: true });
        dispatch(resetState());
      }
    }
  }, [dispatch, navigate, clients]);

  const checkBudgetName = useCallback(async (budgetName, budgetId = null) => {
    const isModeEditBudget = budgetId && !isNaN(budgetId);
    const bodyCheckBudgetName = {
      budgetName: budgetName.trim(),
      ...(isModeEditBudget && { budgetId }),
    };

    try {
      await api.CHECK_BUDGET_NAME(bodyCheckBudgetName);
      
      dispatch(setCurrentStep(3));
      
      if (isModeEditBudget) {
        navigate(`/create-budget/campaigns/${budgetId}`);
      } else {
        navigate(`${CREATE_BUDGET_PATH}/campaigns`);
      }
      
      return { success: true };
    } catch (err) {
      const { response } = err;
      const message = response.data.name[0];

      if (
        message === MESSAGE_ERROR.DUPLICATE_NAME_CLIENT &&
        response.status === 400
      ) {
        dispatch(setUniqueNameError('A budget with this name already exists'));
        return { success: false, error: 'DUPLICATE_NAME' };
      } else {
        error(err);
        return { success: false, error: 'GENERAL_ERROR' };
      }
    }
  }, [dispatch, navigate]);

  return {
    fetchBudgetData,
    checkBudgetName,
  };
};